import { Badge, Icons } from '@commenda/kakashi';
import { Currency } from '@prisma/client';
import { useState } from 'react';
import type { JurisdictionLine, LineItemSummary, TaxSummaryView } from './types';
import { cn, formatFractionAsPercent, toTitleCaseSentence } from '@commenda/utils';
import { CurrencyHelper } from '@commenda/utils/currency';
import { IndirectTaxJurisdictionType } from '../filings';
import { TaxClass } from '../transactions/types';

function JurisdictionTable({
  jurisdictions,
  currency,
}: {
  jurisdictions: JurisdictionLine[];
  currency: string;
}) {
  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-[1fr_auto_auto] gap-x-3xl items-center py-sm border-b border-border-primary mb-2">
        <span className="txt-extra-small-bold text-text-primary">Jurisdiction</span>
        <span className="txt-extra-small-bold text-text-primary text-right">Rate</span>
        <span className="txt-extra-small-bold text-text-primary text-right min-w-[64px]">
          Tax
        </span>
      </div>
      {jurisdictions.map((j) => (
        <div
          key={`${j.name}-${j.type}`}
          className="grid grid-cols-[1fr_auto_auto] gap-x-3xl items-center py-sm"
        >
          <span className="txt-small-regular text-text-primary">{toTitleCaseSentence(j.name)}</span>
          <span className="txt-small-regular text-text-primary text-right">
            {formatFractionAsPercent(j.effective_tax_rate, { maximumFractionDigits: 4 })}
          </span>
          <span className="txt-small-regular text-text-primary text-right min-w-[64px]">
            {CurrencyHelper.formatCurrency(currency as Currency, j.tax, { maximumFractionDigits: 2 })}
          </span>
        </div>
      ))}
    </div>
  );
}

export function TaxBreakdown({ summary }: { summary: TaxSummaryView }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getBadgeForLineItem = (line: LineItemSummary) => {
    // Lines outside the US carry no state row (VAT returns COUNTRY / TRADEBLOC), so their first row decides.
    const stateBreakdown =
      line.jurisdictions.find((each) => each.type === IndirectTaxJurisdictionType.STATE_OR_PROVINCE) ??
      line.jurisdictions[0]
    if (stateBreakdown?.tax_class === TaxClass.TAXABLE) return <Badge variant='success' size='sm'>Taxable</Badge>
    else if (stateBreakdown?.tax_class === TaxClass.NON_TAXABLE) return <Badge variant='warning' size='sm'>Non-Taxable</Badge>
    else if (stateBreakdown?.tax_class === TaxClass.EXEMPT) return <Badge variant='gray' size='sm'>Exempt</Badge>
  }

  const getTotalTaxForLineItem = (lineItem: LineItemSummary) => {
    return lineItem.jurisdictions.reduce((total, each) => total + each.tax, 0)
  }

  return (
    <div className="flex flex-col gap-8">
      {/* Aggregated jurisdictions + total */}
      <div className="flex flex-col">
        <JurisdictionTable
          jurisdictions={summary.jurisdictions}
          currency={summary.currency}
        />
        <div className="grid grid-cols-[1fr_auto_auto] gap-x-3xl items-center py-md border-t border-border-primary mt-sm">
          <span className="txt-small-bold text-text-primary">Total</span>
          <span className="txt-small-bold text-text-primary text-right">
            {formatFractionAsPercent(summary.effectiveRate, { maximumFractionDigits: 4 })}
          </span>
          <span className="txt-small-bold text-text-primary text-right min-w-[64px]">
            {CurrencyHelper.formatCurrency(summary.currency as Currency, summary.salesTax, { maximumFractionDigits: 2 })}
          </span>
        </div>
      </div>

      {/* By line item */}
      <div className='flex flex-col gap-1'>
        <span className="flex txt-small-medium text-text-tertiary mb-3">By line item</span>
        {summary.lineItems.map((eachLineItem) => {
          const isOpen = expanded.has(eachLineItem.lineItemId);
          return (
            <div key={eachLineItem.lineItemId} className="flex flex-col gap-1">
              <button
                type="button"
                onClick={() => toggle(eachLineItem.lineItemId)}
                className={cn("grid grid-cols-[1fr_auto_auto] gap-x-3xl items-center p-2 -mx-sm rounded-sm hover:bg-bg-secondary transition-colors text-left", isOpen ? 'bg-bg-secondary' : undefined)}
              >
                <span className="flex items-center gap-sm min-w-0">
                  {isOpen ? (
                    <Icons.ChevronDown className="size-4 shrink-0 text-fg-quaternary" />
                  ) : (
                    <Icons.ChevronRight className="size-4 shrink-0 text-fg-quaternary" />
                  )}
                  <span className="txt-small-medium text-text-primary">
                    Line {eachLineItem.index}
                  </span>
                  {eachLineItem.taxCode ? (
                    <Badge variant="gray" size="sm">
                      <span className='block max-w-48 truncate'>{eachLineItem.taxCode}</span>
                    </Badge>
                  ) : null}
                  {getBadgeForLineItem(eachLineItem)}
                </span>
                <span className="txt-small-regular text-text-secondary text-right">
                  {formatFractionAsPercent(eachLineItem.effectiveRate, { maximumFractionDigits: 4 })}
                </span>
                <span className="txt-small-regular text-text-primary text-right min-w-[64px]">
                  {CurrencyHelper.formatCurrency(summary.currency as Currency, getTotalTaxForLineItem(eachLineItem), { maximumFractionDigits: 2 })}
                </span>
              </button>
              {isOpen ? (
                <div>
                  <JurisdictionTable
                    jurisdictions={eachLineItem.jurisdictions}
                    currency={summary.currency}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
